function normalizeList(value) {
  return Array.isArray(value) ? value : [];
}

function isOpenTask(task) {
  return task && task.status !== "completed";
}

function hasOutboundMessage(messages) {
  return normalizeList(messages).some((item) => String(item?.direction || "").toLowerCase() === "outbound");
}

function latestActionKind(latestMutation) {
  return String(latestMutation?.kind || "").trim().toLowerCase();
}

function taskImpact(latestMutation, openTaskCount) {
  const completed = normalizeList(latestMutation?.taskIdsCompleted).length;
  const created = latestMutation?.taskIdCreated ? 1 : 0;
  if (openTaskCount > 0) {
    return `${openTaskCount} open`;
  }
  if (created > 0) {
    return "1 created";
  }
  if (completed > 0) {
    return `${completed} completed`;
  }
  return "None open";
}

function step(key, label, complete, active, value, meta) {
  return {
    key,
    label,
    value,
    meta,
    status: complete ? "complete" : active ? "active" : "pending",
    tone: complete ? "ready" : active ? "stale" : "muted",
  };
}

export function buildOperatorWorkflowGuide(options = {}) {
  const settings = Object.assign(
    {
      conversation: null,
      messages: [],
      tasks: [],
      latestMutation: null,
      recentActions: [],
      detailActions: {},
    },
    options
  );
  const conversation = settings.conversation || {};
  const messages = normalizeList(settings.messages);
  const tasks = normalizeList(settings.tasks);
  const latestMutation = settings.latestMutation || null;
  const detailActions = settings.detailActions || {};
  const openTaskCount = tasks.filter(isOpenTask).length;
  const mutationKind = latestActionKind(latestMutation);
  const assigned = Boolean(conversation.assigned_user_id);
  const threadLoaded = messages.length > 0;
  const responseQueued = mutationKind === "reply" || hasOutboundMessage(messages);
  const outcomeSaved = mutationKind === "disposition" || conversation.status === "closed";
  const followUpPlanned = openTaskCount > 0 || Boolean(latestMutation?.taskIdCreated);
  const teamVisible = normalizeList(settings.recentActions).length > 0 || ["reply", "disposition", "task", "note"].includes(mutationKind);
  const operatorProgressed = responseQueued || outcomeSaved || followUpPlanned || Boolean(latestMutation);
  const assignmentComplete = assigned || operatorProgressed;

  const steps = [
    step(
      "assignment",
      "Assigned record",
      assignmentComplete,
      Boolean(detailActions.canClaim) && !operatorProgressed,
      assigned ? "Assigned" : operatorProgressed ? "Worked" : "Unassigned",
      assigned
        ? "Ready for direct work"
        : operatorProgressed
          ? "Operator activity recorded for this record"
          : detailActions.canClaim
            ? "Claimable from queue"
            : "Review only"
    ),
    step(
      "thread",
      "Message thread",
      threadLoaded,
      assigned,
      `${messages.length} message${messages.length === 1 ? "" : "s"}`,
      threadLoaded ? "Thread loaded" : "Waiting for message context"
    ),
    step(
      "reply",
      "Response",
      responseQueued,
      threadLoaded && Boolean(detailActions.canReply),
      responseQueued ? "Queued" : detailActions.canReply ? "Open" : "Locked",
      responseQueued ? "Outbound job recorded" : "No response recorded in this workspace"
    ),
    step(
      "outcome",
      "Outcome",
      outcomeSaved,
      responseQueued && Boolean(detailActions.canDisposition),
      outcomeSaved ? "Saved" : "Open",
      outcomeSaved ? `Conversation ${conversation.status || "updated"}` : "No disposition saved"
    ),
    step(
      "follow_up",
      "Follow-up",
      followUpPlanned,
      outcomeSaved || responseQueued,
      taskImpact(latestMutation, openTaskCount),
      followUpPlanned ? "Reminder coverage exists" : "No reminder coverage yet"
    ),
    step(
      "visibility",
      "Manager visibility",
      teamVisible,
      outcomeSaved || responseQueued || followUpPlanned,
      teamVisible ? "Visible" : "Pending",
      teamVisible ? "Latest action is available to the team" : "No operator action recorded yet"
    ),
  ];
  const completedCount = steps.filter((item) => item.status === "complete").length;
  const nextStep = steps.find((item) => item.status !== "complete") || null;
  const primaryAction = !conversation.id
    ? "Open assigned record"
    : detailActions.canClaim && !assignmentComplete
      ? "Claim record"
      : !threadLoaded
        ? "Review thread"
        : !responseQueued && detailActions.canReply
          ? "Queue response"
          : !outcomeSaved && detailActions.canDisposition
            ? "Save outcome"
            : !followUpPlanned
              ? "Create reminder"
              : "Ready for next record";

  return {
    completedCount,
    totalCount: steps.length,
    status: completedCount === steps.length ? "ready" : "in_progress",
    primaryAction,
    steps,
    summaryCards: [
      {
        label: "Current focus",
        value: primaryAction,
        detail: nextStep ? `${nextStep.label}: ${nextStep.value}` : "Record workflow is complete",
        tone: completedCount === steps.length ? "ready" : "stale",
      },
      {
        label: "Workflow state",
        value: `${completedCount}/${steps.length}`,
        detail: `${steps.length - completedCount} remaining`,
        tone: completedCount === steps.length ? "ready" : completedCount > 0 ? "stale" : "muted",
      },
      {
        label: "Latest action",
        value: latestMutation?.summary || "None recorded",
        detail: latestMutation?.at || "No session mutation yet",
        tone: latestMutation ? latestMutation.tone || "ready" : "muted",
      },
    ],
  };
}
